import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../../../firebaseConfig';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useSimplifiedSecurity } from '../../../hooks/useSimplifiedSecurity';

export const useProducts = () => {
  const { organizationId, isApproved } = useOrganization();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use simplified security for permissions
  const { canManageInventory, canViewInventory } = useSimplifiedSecurity();

  // Fetch products with real-time updates
  useEffect(() => {
    if (!organizationId || !isApproved || !canViewInventory) {
      setProducts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const productsRef = collection(db, `organizations/${organizationId}/products`);
      const productsQuery = query(productsRef, orderBy('createdAt', 'desc'));

      const unsubscribe = onSnapshot(
        productsQuery,
        (snapshot) => {
          const productsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
            updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
          }));
          setProducts(productsData);
          setLoading(false);
        },
        (error) => {
          setError(`Failed to fetch products: ${error.message}`);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (error) {
      setError(`Failed to set up products listener: ${error.message}`);
      setLoading(false);
    }
  }, [organizationId, isApproved, canViewInventory]);

  // Add a new product
  const addProduct = useCallback(async (productData) => {
    if (!organizationId || !isApproved || !canManageInventory) {
      throw new Error('You do not have permission to add products');
    }

    try {
      setError(null);
      const productsRef = collection(db, `organizations/${organizationId}/products`);
      
      // Prepare product data based on whether it has variants
      const newProduct = {
        ...productData,
        hasVariants: productData.hasVariants || false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null
      };

      // For simple products, ensure we have basic fields
      if (!newProduct.hasVariants) {
        newProduct.currentStock = newProduct.currentStock || 0;
        newProduct.minStock = newProduct.minStock || 0;
        newProduct.price = newProduct.price || 0;
      }

      // For complex products, ensure variants are properly structured
      if (newProduct.hasVariants && newProduct.variants) {
        newProduct.variants = newProduct.variants.map(variant => ({
          ...variant,
          id: variant.id || `${newProduct.name?.toLowerCase().replace(/\s+/g, '-')}-${variant.size?.toLowerCase()}-${variant.color?.toLowerCase()}`,
          currentStock: variant.currentStock || 0,
          minStock: variant.minStock || 0,
          price: variant.price || 0
        }));
      }

      const docRef = await addDoc(productsRef, newProduct);
      return { id: docRef.id, ...newProduct };
    } catch (error) {
      setError(`Failed to add product: ${error.message}`);
      throw error;
    }
  }, [organizationId, isApproved, canManageInventory]);

  // Update an existing product
  const updateProduct = useCallback(async (productId, updates) => {
    if (!organizationId || !isApproved || !canManageInventory) {
      throw new Error('You do not have permission to update products');
    }

    try {
      setError(null);
      const productRef = doc(db, `organizations/${organizationId}/products/${productId}`);
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
        lastModifiedBy: auth.currentUser?.uid || null
      };

      await updateDoc(productRef, updateData);
      return { id: productId, ...updateData };
    } catch (error) {
      setError(`Failed to update product: ${error.message}`);
      throw error;
    }
  }, [organizationId, isApproved, canManageInventory]);

  // Delete a product
  const deleteProduct = useCallback(async (productId) => {
    if (!organizationId || !isApproved || !canManageInventory) {
      throw new Error('You do not have permission to delete products');
    }

    try {
      setError(null);
      const productRef = doc(db, `organizations/${organizationId}/products/${productId}`);
      await deleteDoc(productRef);
    } catch (error) {
      setError(`Failed to delete product: ${error.message}`);
      throw error;
    }
  }, [organizationId, isApproved, canManageInventory]);

  // Update product stock (for simple products)
  const updateProductStock = useCallback(async (productId, newStock, operation = 'adjustment') => {
    if (!organizationId || !isApproved || !canManageInventory) {
      throw new Error('You do not have permission to update product stock');
    }

    try {
      setError(null);
      const productRef = doc(db, `organizations/${organizationId}/products/${productId}`);
      
      const updateData = {
        currentStock: newStock,
        updatedAt: serverTimestamp(),
        lastStockUpdate: serverTimestamp(),
        lastStockOperation: operation,
        lastModifiedBy: auth.currentUser?.uid || null
      };

      await updateDoc(productRef, updateData);
      return { id: productId, ...updateData };
    } catch (error) {
      setError(`Failed to update product stock: ${error.message}`);
      throw error;
    }
  }, [organizationId, isApproved, canManageInventory]);

  // Update variant stock (for complex products)
  const updateVariantStock = useCallback(async (productId, variantId, newStock, operation = 'adjustment') => {
    if (!organizationId || !isApproved || !canManageInventory) {
      throw new Error('You do not have permission to update variant stock');
    }

    try {
      setError(null);
      const productRef = doc(db, `organizations/${organizationId}/products/${productId}`);
      
      // Get current product to update specific variant
      const product = products.find(p => p.id === productId);
      if (!product || !product.hasVariants) {
        throw new Error('Product not found or does not have variants');
      }

      const updatedVariants = product.variants.map(variant => 
        variant.id === variantId 
          ? { 
              ...variant, 
              currentStock: newStock,
              updatedAt: new Date().toISOString(),
              lastStockUpdate: new Date().toISOString(),
              lastStockOperation: operation
            }
          : variant
      );

      const updateData = {
        variants: updatedVariants,
        updatedAt: serverTimestamp(),
        lastModifiedBy: auth.currentUser?.uid || null
      };

      await updateDoc(productRef, updateData);
      return { id: productId, variantId, currentStock: newStock };
    } catch (error) {
      setError(`Failed to update variant stock: ${error.message}`);
      throw error;
    }
  }, [organizationId, isApproved, canManageInventory, products]);

  // Get product by ID
  const getProductById = useCallback((productId) => {
    return products.find(product => product.id === productId);
  }, [products]);

  // Get products by category
  const getProductsByCategory = useCallback((category) => {
    return products.filter(product => product.category === category);
  }, [products]);

  // Get simple products (no variants)
  const getSimpleProducts = useCallback(() => {
    return products.filter(product => !product.hasVariants);
  }, [products]);

  // Get complex products (with variants)
  const getComplexProducts = useCallback(() => {
    return products.filter(product => product.hasVariants);
  }, [products]);

  // Get low stock products
  const getLowStockProducts = useCallback(() => {
    const lowStockProducts = [];
    
    products.forEach(product => {
      if (!product.hasVariants) {
        // Simple product
        if (product.currentStock <= product.minStock) {
          lowStockProducts.push(product);
        }
      } else {
        // Complex product - check each variant
        product.variants?.forEach(variant => {
          if (variant.currentStock <= variant.minStock) {
            lowStockProducts.push({
              ...product,
              variant: variant,
              isVariant: true
            });
          }
        });
      }
    });
    
    return lowStockProducts;
  }, [products]);

  // Get out of stock products
  const getOutOfStockProducts = useCallback(() => {
    const outOfStockProducts = [];
    
    products.forEach(product => {
      if (!product.hasVariants) {
        // Simple product
        if (product.currentStock <= 0) {
          outOfStockProducts.push(product);
        }
      } else {
        // Complex product - check each variant
        product.variants?.forEach(variant => {
          if (variant.currentStock <= 0) {
            outOfStockProducts.push({
              ...product,
              variant: variant,
              isVariant: true
            });
          }
        });
      }
    });
    
    return outOfStockProducts;
  }, [products]);

  // Search products
  const searchProducts = useCallback((searchTerm) => {
    if (!searchTerm.trim()) return products;
    
    const term = searchTerm.toLowerCase();
    return products.filter(product => {
      // Search in product name and category
      const matchesProduct = product.name.toLowerCase().includes(term) ||
                           product.category.toLowerCase().includes(term);
      
      if (matchesProduct) return true;
      
      // For complex products, also search in variants
      if (product.hasVariants && product.variants) {
        return product.variants.some(variant => 
          variant.size?.toLowerCase().includes(term) ||
          variant.color?.toLowerCase().includes(term) ||
          variant.sku?.toLowerCase().includes(term)
        );
      }
      
      return false;
    });
  }, [products]);

  // Get unique categories
  const getCategories = useCallback(() => {
    const categories = [...new Set(products.map(product => product.category))];
    return categories.filter(Boolean).sort();
  }, [products]);

  // Get all variants from all products
  const getAllVariants = useCallback(() => {
    const allVariants = [];
    
    products.forEach(product => {
      if (product.hasVariants && product.variants) {
        product.variants.forEach(variant => {
          allVariants.push({
            ...variant,
            productId: product.id,
            productName: product.name,
            productCategory: product.category
          });
        });
      }
    });
    
    return allVariants;
  }, [products]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Data
    products,
    loading,
    error,
    
    // Permissions
    canManageInventory,
    canViewInventory,
    
    // Actions
    addProduct,
    updateProduct,
    deleteProduct,
    updateProductStock,
    updateVariantStock,
    
    // Queries
    getProductById,
    getProductsByCategory,
    getSimpleProducts,
    getComplexProducts,
    getLowStockProducts,
    getOutOfStockProducts,
    searchProducts,
    getCategories,
    getAllVariants,
    
    // Utilities
    clearError
  };
}; 